import React, { useCallback, useEffect, useState } from "react";
import { api, inr, imageUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { ChevronLeft, ChevronRight, Filter, Search, X, ChevronDown, ChevronUp, Truck, Package, Sparkles, Home, Clock, Ban, CheckCircle2, MapPin, Share2, ExternalLink } from "lucide-react";

const STATUSES = [
  { key: "all", label: "All", color: "text-hyd-text", chip: "border-hyd-border" },
  { key: "awaiting", label: "Awaiting", color: "text-hyd-gold", chip: "border-hyd-gold/60 bg-hyd-gold/10" },
  { key: "confirmed", label: "Confirmed", color: "text-blue-300", chip: "border-blue-500/50 bg-blue-500/10" },
  { key: "packed", label: "Packed", color: "text-blue-300", chip: "border-blue-500/50 bg-blue-500/10" },
  { key: "dispatched", label: "Dispatched", color: "text-indigo-300", chip: "border-indigo-500/50 bg-indigo-500/10" },
  { key: "delivered", label: "Delivered", color: "text-emerald-300", chip: "border-emerald-500/50 bg-emerald-500/10" },
  { key: "cancelled", label: "Cancelled", color: "text-red-300", chip: "border-red-500/50 bg-red-500/10" },
];
const NEXT_STATUS = ["awaiting", "confirmed", "packed", "dispatched", "delivered", "cancelled"];

const PAGE_SIZE = 10;

export default function AdminOrders() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    status: "awaiting",
    order_number: "",
    phone: "",
    product: "",
    date_from: "",
    date_to: "",
  });
  const [expanded, setExpanded] = useState(null); // order id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page, page_size: PAGE_SIZE };
      Object.keys(params).forEach((k) => { if (params[k] === "" || params[k] == null) delete params[k]; });
      const [rowsRes, countsRes] = await Promise.all([
        api.get("/admin/orders", { params }),
        api.get("/admin/orders/status-counts"),
      ]);
      setRows(rowsRes.data.items);
      setTotal(rowsRes.data.total);
      setCounts(countsRes.data);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success(`Order ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const setF = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const clearFilters = () => { setPage(1); setFilters({ status: "all", order_number: "", phone: "", product: "", date_from: "", date_to: "" }); };
  const hasFilters = Object.values(filters).some((v) => v && v !== "all");

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Orders</h1>
          <p className="text-hyd-text2 text-sm">{counts.all || 0} total · {counts.awaiting || 0} awaiting confirmation</p>
        </div>
        {hasFilters && (
          <Button variant="ghost" onClick={clearFilters} className="text-hyd-red hover:bg-hyd-panel rounded-full">
            <X className="w-4 h-4 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {/* Status filter chips */}
      <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
        {STATUSES.map((s) => {
          const active = filters.status === s.key;
          const count = counts[s.key] || 0;
          return (
            <button
              key={s.key}
              data-testid={ADMIN.orderStatusChip(s.key)}
              onClick={() => setF("status", s.key)}
              className={`shrink-0 px-3 py-2 rounded-2xl border text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${
                active ? `${s.chip} ${s.color}` : "border-hyd-border bg-hyd-panel text-hyd-text2 hover:text-hyd-gold"
              }`}
            >
              <span>{s.label}</span>
              <span className={`min-w-[22px] h-5 grid place-items-center rounded-full text-[10px] ${active ? "bg-hyd-bg text-hyd-text" : "bg-hyd-bg text-hyd-text2"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search filters */}
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-2">
        <FilterField icon={Search} label="Order #">
          <Input data-testid={ADMIN.orderFilterNumber} value={filters.order_number} onChange={(e) => setF("order_number", e.target.value)} placeholder="HYD..." />
        </FilterField>
        <FilterField icon={Search} label="Phone">
          <Input data-testid={ADMIN.orderFilterPhone} value={filters.phone} onChange={(e) => setF("phone", e.target.value)} placeholder="9999" />
        </FilterField>
        <FilterField icon={Search} label="Product name">
          <Input data-testid={ADMIN.orderFilterProduct} value={filters.product} onChange={(e) => setF("product", e.target.value)} placeholder="Sparklers" />
        </FilterField>
        <FilterField icon={Filter} label="From date">
          <Input data-testid={ADMIN.orderFilterFrom} type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} />
        </FilterField>
        <FilterField icon={Filter} label="To date">
          <Input data-testid={ADMIN.orderFilterTo} type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} />
        </FilterField>
      </div>

      {/* Table */}
      <div className="mt-4 bg-hyd-panel border border-hyd-border rounded-3xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-hyd-text2">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-hyd-text3">No orders match these filters.</div>
        ) : (
          <div className="divide-y divide-hyd-border">
            {rows.map((o) => {
              const open = expanded === o.id;
              return (
                <div key={o.id} data-testid={ADMIN.orderRow(o.id)}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpanded(open ? null : o.id)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(open ? null : o.id); } }}
                    className="w-full text-left px-4 py-3 hover:bg-hyd-panel2/60 flex items-center gap-3 flex-wrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-hyd-gold/40 rounded-none"
                  >
                    <div className="min-w-[140px]">
                      <div className="font-mono text-xs text-hyd-text2">{o.order_number}</div>
                      <div className="text-[10px] text-hyd-text3">{new Date(o.created_at).toLocaleString()}</div>
                    </div>
                    <div className="min-w-[160px] text-sm">
                      <div className="font-medium">{o.user_name}</div>
                      <div className="text-xs text-hyd-text3">{o.user_phone} · {o.address.city}, {o.address.pincode}</div>
                    </div>
                    <div className="text-xs text-hyd-text2 hidden md:block">{o.items.length} items</div>
                    <div className="text-hyd-gold font-bold text-sm min-w-[80px] text-right">{inr(o.total)}</div>
                    <StatusBadge status={o.status} />
                    <div className="ml-auto flex items-center gap-2">
                      {o.status === "awaiting" && (
                        <Button
                          data-testid={ADMIN.orderConfirmBtn(o.id)}
                          onClick={(e) => { e.stopPropagation(); changeStatus(o.id, "confirmed"); }}
                          className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim rounded-full h-8 text-xs font-bold"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm
                        </Button>
                      )}
                      {open ? <ChevronUp className="w-4 h-4 text-hyd-text2" /> : <ChevronDown className="w-4 h-4 text-hyd-text2" />}
                    </div>
                  </div>
                  {open && <OrderDetailPanel order={o} onStatus={changeStatus} />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-hyd-text2">
          Showing {rows.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{(page - 1) * PAGE_SIZE + rows.length} of {total}
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid={ADMIN.orderPagePrev} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} variant="secondary" className="bg-hyd-panel border border-hyd-border text-hyd-text2 rounded-full">
            <ChevronLeft className="w-4 h-4" /> Prev
          </Button>
          <div className="text-xs text-hyd-text2 min-w-[80px] text-center">Page {page} of {totalPages}</div>
          <Button data-testid={ADMIN.orderPageNext} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} variant="secondary" className="bg-hyd-panel border border-hyd-border text-hyd-text2 rounded-full">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function FilterField({ icon: Icon, label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest mb-1 flex items-center gap-1">
        <Icon className="w-3 h-3 text-hyd-gold" /> {label}
      </Label>
      <div className="[&_input]:bg-hyd-bg [&_input]:border-hyd-border [&_input]:text-hyd-text [&_input]:h-9 [&_input]:rounded-xl">
        {children}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    awaiting:   "bg-hyd-gold/20 text-hyd-gold border-hyd-gold/50",
    confirmed:  "bg-blue-500/15 text-blue-300 border-blue-500/40",
    packed:     "bg-blue-500/15 text-blue-300 border-blue-500/40",
    dispatched: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
    delivered:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    cancelled:  "bg-red-500/15 text-red-300 border-red-500/40",
  };
  return (
    <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded-full border ${map[status] || "border-hyd-border"}`}>
      {status}
    </span>
  );
}

function OrderDetailPanel({ order, onStatus }) {
  return (
    <div data-testid={ADMIN.orderDetailPanel} className="bg-hyd-bg/60 border-t border-hyd-border px-4 py-5">
      <div className="grid md:grid-cols-3 gap-4">
        {/* Items */}
        <div className="md:col-span-2">
          <SectionTitle>{order.items.length} items</SectionTitle>
          <div className="mt-2 bg-hyd-panel border border-hyd-border rounded-2xl divide-y divide-hyd-border">
            {order.items.map((i, idx) => (
              <div key={idx} className="p-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-hyd-panel2 overflow-hidden shrink-0 border border-hyd-border">
                  {i.image_url && <img src={imageUrl(i.image_url)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold line-clamp-2">{i.name}</div>
                  <div className="text-xs text-hyd-text3">{i.qty} × {inr(i.unit_price)} · {i.kind}</div>
                </div>
                <div className="text-hyd-gold font-bold text-sm">{inr(i.qty * i.unit_price)}</div>
              </div>
            ))}
          </div>

          {order.notes && (
            <div className="mt-3">
              <SectionTitle>Customer comments</SectionTitle>
              <div className="mt-2 bg-hyd-panel border border-hyd-border rounded-2xl p-3 text-sm text-hyd-text2 whitespace-pre-wrap">
                {order.notes}
              </div>
            </div>
          )}

          <div className="mt-3">
            <SectionTitle>Status history</SectionTitle>
            <div className="mt-2 bg-hyd-panel border border-hyd-border rounded-2xl p-3 space-y-1 text-xs">
              {(order.status_history || []).map((h, idx) => (
                <div key={idx} className="flex justify-between text-hyd-text2">
                  <span className="uppercase tracking-widest font-bold text-hyd-text">{h.status}</span>
                  <span>{new Date(h.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-3">
          <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-3">
            <SectionTitle>Shipping</SectionTitle>
            <div className="mt-2 text-sm">
              <div className="font-semibold">{order.address.name}</div>
              <div className="text-hyd-text2">{order.address.line1}{order.address.line2 ? ", " + order.address.line2 : ""}</div>
              <div className="text-hyd-text2">{order.address.city}, {order.address.state} - {order.address.pincode}</div>
              <div className="text-hyd-text2 mt-1">📞 {order.address.phone}</div>
              {order.address.landmark && <div className="text-hyd-text3 text-xs mt-1">Landmark: {order.address.landmark}</div>}
              {order.delivery_slot && (
                <div className="mt-2 flex items-center gap-1 text-xs text-hyd-gold">
                  <Clock className="w-3.5 h-3.5" /> {order.delivery_slot.replace("|", " · ")}
                </div>
              )}

              <DeliveryLinks order={order} />
            </div>
          </div>

          <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-3">
            <SectionTitle>Payment</SectionTitle>
            <div className="mt-2 space-y-1 text-sm">
              <Row label="Subtotal" value={inr(order.subtotal)} />
              <Row label="Delivery" value={order.delivery_fee === 0 ? "FREE" : inr(order.delivery_fee)} />
              {order.discount > 0 && <Row label={`Coupon (${order.coupon_code || ""})`} value={`- ${inr(order.discount)}`} />}
              <div className="border-t border-hyd-border my-1" />
              <Row label="Total" value={inr(order.total)} bold />
              <div className="text-[10px] uppercase tracking-widest text-hyd-text2 mt-2">Method: {order.payment_method}</div>
            </div>
          </div>

          <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-3">
            <SectionTitle>Actions</SectionTitle>
            <div className="mt-2">
              <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest">Change status</Label>
              <Select value={order.status} onValueChange={(v) => onStatus(order.id, v)}>
                <SelectTrigger data-testid={ADMIN.orderStatusSelect(order.id)} className="bg-hyd-bg border-hyd-border mt-1 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NEXT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[10px] uppercase tracking-widest text-hyd-gold font-bold">{children}</div>;
}

function DeliveryLinks({ order }) {
  const a = order.address || {};
  const hasCoords = a.lat != null && a.lng != null;
  const mapsUrl = hasCoords
    ? `https://www.google.com/maps?q=${a.lat},${a.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(", ")
      )}`;
  const items = (order.items || []).map((i) => `• ${i.name} × ${i.qty}`).join("\n");
  const message = [
    `*Hyderabad Crackers — Delivery Task*`,
    ``,
    `*Order:* ${order.order_number}`,
    `*Customer:* ${a.name} (${a.phone})`,
    `*Address:* ${a.line1}${a.line2 ? ", " + a.line2 : ""}, ${a.city}, ${a.state} - ${a.pincode}`,
    a.landmark ? `*Landmark:* ${a.landmark}` : null,
    hasCoords ? `*GPS:* ${a.lat}, ${a.lng}` : null,
    `*Map:* ${mapsUrl}`,
    ``,
    `*Items:*\n${items}`,
    ``,
    `*Total:* ₹${order.total} (${order.payment_method})`,
    order.notes ? `*Notes:* ${order.notes}` : null,
  ].filter(Boolean).join("\n");
  const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

  return (
    <div className="mt-3 pt-3 border-t border-hyd-border/60 space-y-2">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-hyd-gold font-bold">
        <MapPin className="w-3 h-3" /> Delivery handoff
      </div>
      {hasCoords ? (
        <div className="text-[11px] text-hyd-text3">
          GPS: {Number(a.lat).toFixed(5)}, {Number(a.lng).toFixed(5)}
        </div>
      ) : (
        <div className="text-[11px] text-hyd-text3">No GPS captured — using address search</div>
      )}
      <div className="flex flex-wrap gap-2">
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          data-testid={`admin-order-maps-${order.id}`}
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest bg-hyd-bg border border-hyd-gold/60 text-hyd-gold rounded-full px-3 py-1.5 hover:bg-hyd-gold/10"
        >
          <ExternalLink className="w-3 h-3" /> Open in Maps
        </a>
        <a
          href={waUrl}
          target="_blank"
          rel="noreferrer"
          data-testid={`admin-order-whatsapp-${order.id}`}
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest bg-emerald-500 text-black rounded-full px-3 py-1.5 hover:bg-emerald-400"
        >
          <Share2 className="w-3 h-3" /> Share on WhatsApp
        </a>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-bold text-base" : "text-sm"}`}>
      <span className="text-hyd-text2">{label}</span>
      <span>{value}</span>
    </div>
  );
}
